import { css, useTheme, type SerializedStyles, type Theme } from '@emotion/react';
import { Box, Text, Stack, useBreakpoint } from '@resi-media/resi-ui';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { usePrefix } from '@studio/hooks/use-prefix';
import { NotFoundIcon } from '../NotFoundIcon';

const S = {
  wrapper: (theme: Theme) => css`
    width: 100%;
    max-height: ${theme.typography.pxToRem(443)};
    max-width: ${theme.typography.pxToRem(788)};
    aspect-ratio: 16/9;
    padding: clamp(${theme.spacing.s}, 4%, ${theme.typography.pxToRem(101)})
      clamp(${theme.spacing.x3}, 6%, ${theme.typography.pxToRem(146)});
    box-sizing: border-box;
    background: ${theme.palette.common.black};
    color: ${theme.palette.common.white};
  `,
};

type LivePlayerUnavailableProps = {
  customCss?: SerializedStyles;
};

const LivePlayerUnavailable = ({ customCss }: LivePlayerUnavailableProps) => {
  const { prefixNS } = usePrefix('components:', 'livePlayerUnavailable');
  const theme = useTheme();
  const mediaQuery = useBreakpoint();
  const { error } = useLivePlayer();
  const isDefault = !error?.info?.length;

  return (
    <Stack
      alignItems="center"
      css={[S.wrapper(theme), customCss]}
      dataTestId="live-player-unavailable"
      gap="xl"
      justifyContent="center"
    >
      <NotFoundIcon
        fontSize={mediaQuery.lg ? theme.typography.pxToRem(85) : theme.typography.pxToRem(45)}
        iconRootProps={{ style: { height: 'inherit' } }}
      />
      <Box maxWidth="x88" textAlign="center">
        <Text
          css={(theme) => ({ color: theme.palette.common.white })}
          dataTestId="live-player-unavailable-title"
          variant={mediaQuery.lg ? 'h4' : 'h5'}
        >
          {prefixNS(isDefault ? 'default.title' : 'error.title')}
        </Text>
        <Text
          css={(theme) => ({ color: theme.palette.common.white })}
          dataTestId="live-player-unavailable-description"
          variant={mediaQuery.lg ? 'body2' : 'body3'}
        >
          {prefixNS(isDefault ? 'default.description' : 'error.description')}
        </Text>
      </Box>
    </Stack>
  );
};

LivePlayerUnavailable.displayName = 'LivePlayerUnavailable';

export { LivePlayerUnavailable };
